import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";

import PropTypes from "prop-types";
import { Box, Typography, Paper, Popper } from "@mui/material";

export default function MainTable(props) {
  // Code to create popup items
  function isOverflown(element) {
    return (
      element.scrollHeight > element.clientHeight ||
      element.scrollWidth > element.clientWidth
    );
  }

  const GridCellExpand = React.memo(function GridCellExpand(props) {
    const { width, value } = props;
    const wrapper = React.useRef(null);
    const cellDiv = React.useRef(null);
    const cellValue = React.useRef(null);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [showFullCell, setShowFullCell] = React.useState(false);
    const [showPopper, setShowPopper] = React.useState(false);

    const handleMouseEnter = () => {
      const isCurrentlyOverflown = isOverflown(cellValue.current);
      setShowPopper(isCurrentlyOverflown);
      setAnchorEl(cellDiv.current);
      setShowFullCell(true);
    };

    const handleMouseLeave = () => {
      setShowFullCell(false);
    };

    React.useEffect(() => {
      if (!showFullCell) {
        return undefined;
      }

      function handleKeyDown(nativeEvent) {
        // IE11, Edge (prior to using Bink?) use 'Esc'
        if (nativeEvent.key === "Escape" || nativeEvent.key === "Esc") {
          setShowFullCell(false);
        }
      }

      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [setShowFullCell, showFullCell]);

    return (
      <Box
        ref={wrapper}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          alignItems: "center",
          lineHeight: "24px",
          width: 1,
          height: 1,
          position: "relative",
          display: "flex",
        }}
      >
        <Box
          ref={cellDiv}
          sx={{
            height: 1,
            width,
            display: "block",
            position: "absolute",
            top: 0,
          }}
        />
        <Box
          ref={cellValue}
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value}
        </Box>
        {showPopper && (
          <Popper
            open={showFullCell && anchorEl !== null}
            anchorEl={anchorEl}
            style={{ width, marginLeft: -17 }}
          >
            <Paper
              elevation={1}
              style={{ minHeight: wrapper.current.offsetHeight - 3 }}
            >
              <Typography variant="body2" style={{ padding: 8 }}>
                {value}
              </Typography>
            </Paper>
          </Popper>
        )}
      </Box>
    );
  });

  GridCellExpand.propTypes = {
    value: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
  };

  function renderCellExpand(params) {
    return (
      <GridCellExpand
        value={params.value || ""}
        width={params.colDef.computedWidth}
      />
    );
  }

  // Add columns to your own desire to be displayed in the table
  const columnTableRequested = [
    ["measure_number", "Measure number"],
    ["pub_year", "Year"],
    ["measure_category", "Category of measure"],
    ["measure_name", "Name of measure"],
    ["country", "Country"],
    ["measure_subcategory", "Subcategory"],
    ["measure_type", "Type of measure"],
  ];

  const rowData = [];
  const MeasureData = JSON.parse(sessionStorage.getItem('fullmeasures')) // FetchRequest("fullmeasures");
  if (MeasureData !== null) {
    MeasureData.map((element) => {
      if (
        (String(element[columnTableRequested[4][0]]).includes(
          props.selectedCountry
        ) ||
          props.selectedCountry === "none" ||
          String(element[columnTableRequested[4][0]]).includes(
            "European Union"
          )) &&
        (String(element[columnTableRequested[2][0]]).includes(
          props.selectedCat
        ) ||
          props.selectedCat === null ||
          props.selectedCat === undefined) &&
        (String(element[columnTableRequested[5][0]]).includes(
          props.selectedSubCat
        ) ||
          props.selectedSubCat === null ||
          props.selectedSubCat === undefined) &&
        (String(element[columnTableRequested[6][0]]).includes(
          props.selectedMeas
        ) ||
          props.selectedMeas === null ||
          props.selectedMeas === undefined) &&
        (String(element[columnTableRequested[3][0]]).includes(
          props.selectedTitle
        ) ||
          props.selectedTitle === null ||
          props.selectedTitle === undefined)
      ) {
        let jsonAdd = {
          id: element[columnTableRequested[0][0]], //id of column, in this case measure number
          col1: element[columnTableRequested[1][0]], //Year
          col2: element[columnTableRequested[2][0]], //Category of measure
          col3: element[columnTableRequested[3][0]], // Name of measure
          col4: element[columnTableRequested[4][0]], // Country
          col5: element[columnTableRequested[5][0]], //Sub-category
          col6: element[columnTableRequested[6][0]], //Specific measure
        };
        rowData.push(jsonAdd);
      }
    });
  }

  // Make use of one time adding as const, simply to avoid several 'pushes' and having double rows
  const rows = rowData !== undefined ? rowData : undefined;
  let columns = [];

  if (!props.boolMapMinimalized) {
    //show table columns when map is not minimalized
    columns = [
      {
        field: "col1",
        headerName: columnTableRequested[1][1],
        width: 150,
        flex: 0.05,
        renderCell: renderCellExpand,
      },
      {
        field: "col2",
        headerName: columnTableRequested[2][1],
        width: 150,
        flex: 0.3,
        renderCell: renderCellExpand,
      },
      {
        field: "col3",
        headerName: columnTableRequested[3][1],
        width: 150,
        flex: 1,
        renderCell: renderCellExpand,
      },
      {
        field: "col4",
        headerName: columnTableRequested[4][1],
        width: 200,
        flex: 0.2,
        renderCell: renderCellExpand,
      },
    ];
  } else if (props.boolMapMinimalized) {
    //show table columns if map is minimalized
    columns = [
      {
        field: "col1",
        headerName: columnTableRequested[1][1],
        width: 150,
        flex: 0.05,
        renderCell: renderCellExpand,
      },
      {
        field: "col3",
        headerName: columnTableRequested[3][1],
        width: 150,
        flex: 1,
        renderCell: renderCellExpand,
      },
      {
        field: "col2",
        headerName: columnTableRequested[2][1],
        width: 150,
        flex: 0.3,
        renderCell: renderCellExpand,
      },
      {
        field: "col5",
        headerName: columnTableRequested[5][1],
        width: 150,
        flex: 0.3,
        renderCell: renderCellExpand,
      },
      {
        field: "col6",
        headerName: columnTableRequested[6][1],
        width: 150,
        flex: 0.3,
        renderCell: renderCellExpand,
      },
      {
        field: "col4",
        headerName: columnTableRequested[4][1],
        width: 150,
        flex: 0.2,
        renderCell: renderCellExpand,
      },
    ];
  }

  const [selectedRows, setSelectedRows] = useState();
  useEffect(() => {
    props.selectedRows(selectedRows);
  });

  return (
    <Box height={"60vh"}>
      <DataGrid
        // Start the datagrid with a sorting on a specific column
        initialState={{
          sorting: {
            sortModel: [{ field: "col3", sort: "desc" }],
          },
        }}
        // checkboxSelection
        autoPageSize={true} //Autosize visible rows, depending on window size
        onSelectionModelChange={(ids) => {
          //on selection of a row
          const selectedIDs = new Set(ids);
          const selectedRow = rows.filter((row) => selectedIDs.has(row.id));

          setSelectedRows(selectedRow[0].id); // passthrough of selected row
        }}
        rows={rows}
        columns={columns}
      />
    </Box>
  );
}
